import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, User, Clock, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'resident' | 'vendor';
  content: string;
  timestamp: string;
  isRead: boolean;
}

interface Conversation {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorAvatar?: string;
  vendorRating?: number;
  vendorPhone?: string;
  vendorEmail?: string;
  messages: Message[];
  isActive: boolean;
  lastActivity: string;
}

interface ServiceRequestConversationsProps {
  serviceRequestId: string;
  userRole: 'resident' | 'vendor';
  userId: string;
  conversations: Conversation[];
  onSendMessage?: (conversationId: string, message: string) => void;
  onStartConversation?: (vendorId: string, vendorName: string) => string; // Returns conversation ID
  activeConversationId?: string;
  onConversationChange?: (conversationId: string) => void;
}

export function ServiceRequestConversations({
  serviceRequestId,
  userRole,
  userId,
  conversations = [],
  onSendMessage,
  onStartConversation,
  activeConversationId,
  onConversationChange,
}: ServiceRequestConversationsProps) {
  const [selectedConversationId, setSelectedConversationId] = useState<string>(
    activeConversationId || conversations[0]?.id || '',
  );
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (activeConversationId && activeConversationId !== selectedConversationId) {
      setSelectedConversationId(activeConversationId);
    }
  }, [activeConversationId, selectedConversationId]);

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversationId) return;

    onSendMessage?.(selectedConversationId, newMessage.trim());
    setNewMessage('');
  };

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    onConversationChange?.(conversationId);
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="text-center py-16">
        <MessageSquare className="w-20 h-20 text-muted-foreground mx-auto mb-6" />
        <h3 className="font-semibold mb-3 text-xl">No Messages Yet</h3>
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
          Start a conversation with vendors by clicking the "Message" button on their bids. All your
          conversations for this service request will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="h-[700px] flex border rounded-xl overflow-hidden bg-white shadow-xs">
      {/* Conversation List - Made wider for better vendor name visibility */}
      <div className="w-96 border-r bg-slate-50/50">
        <div className="p-6 border-b bg-white">
          <h3 className="font-semibold text-lg">Conversations</h3>
          <p className="text-sm text-slate-600 mt-1">
            {conversations.length} vendor{conversations.length > 1 ? 's' : ''}
          </p>
        </div>
        <ScrollArea className="h-[640px]">
          <div className="p-4 space-y-3">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`p-4 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedConversationId === conversation.id
                    ? 'bg-blue-50 border border-blue-200 shadow-xs'
                    : 'bg-white border border-gray-100 hover:border-gray-200'
                }`}
                onClick={() => handleConversationSelect(conversation.id)}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12 ring-2 ring-white shadow-xs">
                    <AvatarImage src={conversation.vendorAvatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 font-medium">
                      {conversation.vendorName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-base text-gray-900 leading-tight">
                        {conversation.vendorName}
                      </h4>
                      {conversation.vendorRating && (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-amber-100 text-amber-700 border-amber-200 ml-2 shrink-0"
                        >
                          ⭐ {conversation.vendorRating}
                        </Badge>
                      )}
                    </div>

                    {conversation.messages.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 truncate leading-relaxed">
                          {conversation.messages[conversation.messages.length - 1].content}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {formatDate(conversation.lastActivity)}
                          </span>
                          {conversation.messages.some(
                            (m) => !m.isRead && m.senderId !== userId,
                          ) && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          )}
                        </div>
                      </div>
                    )}

                    {conversation.messages.length === 0 && (
                      <p className="text-xs text-gray-500">Click to start messaging</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Active Conversation */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedConversation ? (
          <>
            {/* Conversation Header */}
            <div className="p-6 border-b bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12 ring-2 ring-blue-100">
                    <AvatarImage src={selectedConversation.vendorAvatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 font-medium">
                      {selectedConversation.vendorName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">
                      {selectedConversation.vendorName}
                    </h3>
                    {selectedConversation.vendorRating && (
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <span className="text-amber-500">⭐</span>
                        {selectedConversation.vendorRating} rating
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  {selectedConversation.vendorPhone && (
                    <Button variant="outline" size="sm" className="bg-white hover:bg-gray-50">
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                  {selectedConversation.vendorEmail && (
                    <Button variant="outline" size="sm" className="bg-white hover:bg-gray-50">
                      <Mail className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-6 bg-gray-50/30">
              <div className="space-y-6 max-w-4xl mx-auto">
                {selectedConversation.messages.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-xs">
                      <MessageSquare className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600">
                      Start the conversation by sending a message below.
                    </p>
                  </div>
                ) : (
                  selectedConversation.messages.map((message, index) => {
                    const isFromUser = message.senderId === userId;
                    const showDate =
                      index === 0 ||
                      formatDate(message.timestamp) !==
                        formatDate(selectedConversation.messages[index - 1].timestamp);

                    return (
                      <div key={message.id} className="space-y-3">
                        {showDate && (
                          <div className="text-center">
                            <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow-xs border">
                              {formatDate(message.timestamp)}
                            </span>
                          </div>
                        )}

                        <div
                          className={`flex gap-4 ${isFromUser ? 'justify-end' : 'justify-start'}`}
                        >
                          {!isFromUser && (
                            <Avatar className="w-10 h-10 ring-2 ring-white shadow-xs">
                              <AvatarImage src={selectedConversation.vendorAvatar} />
                              <AvatarFallback className="bg-blue-100 text-blue-700 text-sm">
                                {selectedConversation.vendorName
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                              </AvatarFallback>
                            </Avatar>
                          )}

                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-xs ${
                              isFromUser
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">{message.senderName}</span>
                              <span
                                className={`text-xs ${
                                  isFromUser ? 'text-blue-100' : 'text-gray-500'
                                }`}
                              >
                                {formatTime(message.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm leading-relaxed">{message.content}</p>
                          </div>

                          {isFromUser && (
                            <Avatar className="w-10 h-10 ring-2 ring-white shadow-xs">
                              <AvatarFallback className="bg-green-100 text-green-700 text-sm font-medium">
                                {userRole === 'resident' ? 'R' : 'V'}
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="p-6 border-t bg-white">
              <div className="flex gap-4">
                <Textarea
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="min-h-[80px] flex-1 resize-none border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="self-end bg-blue-600 hover:bg-blue-700 h-12 px-6 rounded-xl shadow-xs"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50/30">
            <div className="text-center">
              <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-xs">
                <MessageSquare className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-600">Choose a vendor from the list to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
